import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Grid from "@mui/material/Grid"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Chip from "@mui/material/Chip"
import IconButton from "@mui/material/IconButton"
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded"
import CloseIcon from "@mui/icons-material/Close"
import BoltRoundedIcon from "@mui/icons-material/BoltRounded"
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded"
import WebRoundedIcon from "@mui/icons-material/WebRounded"
import Lottie from "lottie-react"
import { Link } from "react-router-dom"
import { alpha, useTheme } from "@mui/material/styles"

interface TrialOverModalProps
{
  open: boolean
  handleClose: () => void
}

export const TrialOverModal = ({ open, handleClose }: TrialOverModalProps) => {
  const theme = useTheme()

  return(
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: styles.dialog(theme) }}
    >
      <DialogTitle sx={styles.titleBar(theme)}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box sx={styles.headerIcon(theme)}>
              <LockClockRoundedIcon />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight={700}>Trial Expired</Typography>
            <Typography variant="body2" sx={styles.subtitle(theme)}>
              Your 30-day web access trial has ended. Trading can continue, but premium web features now require a subscription.
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} sx={styles.closeButton(theme)}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={styles.hero(theme)}>
            <Grid container spacing={2} alignItems="center" >
              <Grid item xs={12} md={7}>
                <Stack spacing={1.25}>
                  <Typography variant="overline" sx={styles.eyebrow(theme)}>Account Access Update</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    Keep the full AlgoTrader web experience live
                  </Typography>
                  <Typography variant="body2" sx={styles.subtitle(theme)}>
                    Your bot will still be trading, but web dashboard access and multi-pair controls are now restricted until a subscription is active.
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip icon={<WebRoundedIcon />} label="Web app access locked" sx={styles.chip(theme)} />
                    <Chip icon={<BoltRoundedIcon />} label="Bot trading still active" sx={styles.chip(theme)} />
                    <Chip icon={<PhoneIphoneRoundedIcon />} label="Use mobile app for single-pair changes" sx={styles.chip(theme)} />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={styles.animationWrap(theme)}>
                  <Lottie animationData={require("../../../animations/person-lottie.json")} loop autoplay style={styles.lottie} />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={styles.section(theme)}>
            <Typography variant="overline" sx={styles.eyebrow(theme)}>What Changed</Typography>
            <Stack spacing={0.8}>
              <Typography variant="body2" sx={styles.copy(theme)}>
                Web app access is no longer included after the free trial period.
              </Typography>
              <Typography variant="body2" sx={styles.copy(theme)}>
                Multi-pair trading support is also restricted until a paid subscription is active.
              </Typography>
              <Typography variant="body2" sx={styles.copy(theme)}>
                If you were using multi-pair trading, update your main single trading pair from the mobile app.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={styles.actions(theme)}>
        <Button onClick={handleClose}>Close</Button>
        <Box flex={1} />
        <Button
          variant="contained"
          component={Link}
          to="/settings"
          sx={styles.primaryButton}
        >
          Setup Subscription
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const styles = {
  dialog: (theme: any) => ({
    borderRadius: "20px",
    overflow: "hidden",
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
        : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  titleBar: (theme: any) => ({
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  headerIcon: (theme: any) => ({
    width: 46,
    height: 46,
    borderRadius: "14px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.warning.main,
    backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  closeButton: (theme: any) => ({
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  hero: (theme: any) => ({
    p: 2,
    borderRadius: "16px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#11243b", 0.88)} 0%, ${alpha("#0c1727", 0.92)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.92)} 0%, ${alpha("#eaf3ff", 0.92)} 100%)`,
  }),
  section: (theme: any) => ({
    p: 1.5,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.58),
  }),
  eyebrow: (theme: any) => ({
    display: "block",
    marginBottom: 6,
    letterSpacing: 1.4,
    color: alpha(theme.palette.text.primary, 0.62),
  }),
  subtitle: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.68),
  }),
  copy: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.78),
  }),
  chip: (theme: any) => ({
    borderRadius: "10px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.72),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    '& .MuiChip-label': {
      fontWeight: 600,
    },
  }),
  animationWrap: (theme: any) => ({
    minHeight: 220,
    display: "grid",
    placeItems: "center",
    borderRadius: "14px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.56),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.4)}`,
  }),
  lottie: {
    width: 180,
    height: 180,
  },
  actions: (theme: any) => ({
    px: 3,
    py: 2,
    borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02),
  }),
  primaryButton: {
    borderRadius: "12px",
    boxShadow: "none",
    minWidth: 170,
  },
}
